import React from "react";
//#region types
import { WintSuperkollAPIModelTimeReporting } from "wint-api-client/dist/types";
import { MoreActionMenuItemType } from "models/propTypes/menuPropTypes";
import { IconDefinitionConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import { WeeklyMoreActionProps } from "models/propTypes/timeReportingPropTypes";
//#endregion
//#region components
import { MoreActions } from "components/common";
import { useTimeReportingService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const WeeklyTimeReportMoreActions: React.FC<WeeklyMoreActionProps> = (props) => {
  const timeReportingService = useTimeReportingService();
  const { weekSegments, employeeId, reloadHandler, isLastWeek, monthStatus } = props;

  const onWeekStatusChangeClick = async (week: WintSuperkollAPIModelTimeReporting.WeekSegment, lock: boolean) => {
    const model: WintSuperkollAPIModelTimeReporting.UpdateStatusByDateRange = {
      employeeId: employeeId,
      startDate: week.weekSegmentStart,
      endDate: week.weekSegmentEnd,
      status: lock
        ? WintSuperkollAPIModelTimeReporting.TimeReportStatus.Locked
        : WintSuperkollAPIModelTimeReporting.TimeReportStatus.Open,
    };
    props.onCreateOrUpdateWeek && (await props.onCreateOrUpdateWeek());
    return timeReportingService.updateStatus(model);
  };

  const lockWeekAction: MoreActionMenuItemType = {
    description: `${LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.TimeReporting.LockWeek
    )} ${timeReportingService.getWeekDateDisplay(weekSegments && weekSegments[1])}`,
    icon: IconDefinitionConstants.WLock,
    onClickHandler: weekSegments && onWeekStatusChangeClick.bind(null, weekSegments[1], true),
    successMessage: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.LockWeekSuccess),
    onActionSuccess: reloadHandler,
  };

  const unLockWeekAction: MoreActionMenuItemType = {
    description: `${LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.TimeReporting.UnLockWeek
    )} ${timeReportingService.getWeekDateDisplay(weekSegments && weekSegments[1])}`,
    icon: IconDefinitionConstants.WLock,
    onClickHandler: weekSegments && onWeekStatusChangeClick.bind(null, weekSegments[1], false),
    successMessage: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.LockWeekSuccess),
    onActionSuccess: reloadHandler,
  };

  const lockPreviousWeekAction: MoreActionMenuItemType = {
    description: `${LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.TimeReporting.LockWeek
    )} ${timeReportingService.getWeekDateDisplay(weekSegments && weekSegments[0])}`,
    icon: IconDefinitionConstants.WLock,
    onClickHandler: weekSegments && onWeekStatusChangeClick.bind(null, weekSegments[0], true),
    successMessage: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.TimeReporting.LockWeekSuccess),
    onActionSuccess: reloadHandler,
  };

  const unLockPreviousWeekAction: MoreActionMenuItemType = {
    description: `${LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.TimeReporting.UnLockWeek
    )} ${timeReportingService.getWeekDateDisplay(weekSegments && weekSegments[0])}`,
    icon: IconDefinitionConstants.WLock,
    onClickHandler: weekSegments && onWeekStatusChangeClick.bind(null, weekSegments[0], false),
    successMessage: LocalizationHelperInstance.getFormattedMessage(
      TranslationKeys.TimeReporting.UnLockPreviousWeekSuccess
    ),
    onActionSuccess: reloadHandler,
  };

  const getMenuItems = React.useMemo((): MoreActionMenuItemType[] => {
    const actions: MoreActionMenuItemType[] = [];
    if (isLastWeek && weekSegments) {
      if (weekSegments.length === 2) {
        const previousWeekDisplayLock = timeReportingService.getDisplayLockStatus(weekSegments[0].weekSegmentStatus);
        if (previousWeekDisplayLock !== undefined) {
          previousWeekDisplayLock ? actions.push(lockPreviousWeekAction) : actions.push(unLockPreviousWeekAction);
        }
        const nextWeekDisplayLock = timeReportingService.getDisplayLockStatus(weekSegments[1].weekSegmentStatus);
        if (
          nextWeekDisplayLock !== undefined &&
          monthStatus !== undefined &&
          monthStatus === WintSuperkollAPIModelTimeReporting.TimeReportStatus.Open
        ) {
          if (nextWeekDisplayLock) {
            actions.push(lockWeekAction);
          } else {
            actions.push(unLockWeekAction);
          }
        }
      } else if (
        weekSegments.length === 1 &&
        // eslint-disable-next-line eqeqeq --- supressed to avoid regression testing if changed
        monthStatus != undefined &&
        monthStatus === WintSuperkollAPIModelTimeReporting.TimeReportStatus.Open
      ) {
        const lastWeekDisplayLock = timeReportingService.getDisplayLockStatus(weekSegments[0].weekSegmentStatus);
        if (lastWeekDisplayLock !== undefined) {
          actions.push(lastWeekDisplayLock ? lockPreviousWeekAction : unLockPreviousWeekAction);
        }
      }
    }
    return actions;
  }, [monthStatus, isLastWeek, props.isValid, weekSegments]);

  return <MoreActions menuItems={getMenuItems} />;
};

export default WeeklyTimeReportMoreActions;
